import { Calendar, Clock, MapPin } from 'lucide-react'
import { formatEventDay } from '~/features/events/lib/event-date-format'
import * as m from '~/paraglide/messages'
import { countryDisplayName } from '~/shared/lib/countries'
import { scrollToFormField } from '~/shared/lib/form-utils'
import { richTextToPlainText } from '~/shared/lib/rich-text'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'

interface EventPreviewProps {
  title: string
  description: string
  startTime: string
  location: {
    name?: string
    street: string
    city: string
    country: string
  }
  price: {
    kind: 'unknown' | 'free' | 'fixed' | 'range'
    priceFixed?: number
    priceRangeMin?: number
    priceRangeMax?: number
  }
  coverImageUrl?: string
  onFieldClick?: (fieldName: string) => void
}

export function EventPreview({
  title,
  description,
  startTime,
  location,
  price,
  coverImageUrl,
  onFieldClick,
}: EventPreviewProps) {
  const formatPrice = () => {
    if (price.kind === 'unknown') return m.priceUnknown()
    if (price.kind === 'free') return m.free()
    if (price.kind === 'fixed' && price.priceFixed) return `€${price.priceFixed}`
    if (price.kind === 'range' && price.priceRangeMin && price.priceRangeMax) {
      return `€${price.priceRangeMin} - €${price.priceRangeMax}`
    }
    return m.priceNotSet()
  }

  const handleClick = (fieldName: string) => {
    if (!onFieldClick) return
    onFieldClick(fieldName)
    scrollToFormField(fieldName)
  }

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => handleClick('coverImageUrl')}
        className={cn(
          'w-full transition-opacity',
          onFieldClick && 'cursor-pointer hover:opacity-80',
        )}
      >
        {coverImageUrl && coverImageUrl.trim() !== '' ? (
          <div className="aspect-[2/1] w-full overflow-hidden bg-muted">
            <img
              src={coverImageUrl}
              alt={title || m.eventPreview()}
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[2/1] w-full bg-muted/50 flex items-center justify-center">
            <Calendar className="h-12 w-12 text-muted-foreground/40" />
          </div>
        )}
      </button>
      <CardHeader className="p-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={() => handleClick('title')}
            className={cn(
              'text-left transition-colors',
              onFieldClick && 'cursor-pointer hover:text-orange-600',
            )}
          >
            <CardTitle className="line-clamp-2 text-sm">{title || m.eventTitle()}</CardTitle>
          </button>
          <Badge variant="secondary" className="text-xs shrink-0">
            {m.draft()}
          </Badge>
        </div>
        <button
          type="button"
          onClick={() => handleClick('description')}
          className={cn(
            'text-left transition-colors',
            onFieldClick && 'cursor-pointer hover:text-orange-600',
          )}
        >
          <CardDescription className="line-clamp-2 text-xs">
            {richTextToPlainText(description) || m.eventDescriptionWillAppearHere()}
          </CardDescription>
        </button>
      </CardHeader>
      <CardContent className="space-y-2 p-3 pt-0">
        <button
          type="button"
          onClick={() => handleClick('startTime')}
          className={cn(
            'flex items-center gap-2 text-xs w-full text-left transition-colors',
            onFieldClick && 'cursor-pointer hover:text-orange-600',
          )}
        >
          <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="truncate">
            {startTime ? formatEventDay(new Date(startTime)) : m.dateNotSet()}
          </span>
        </button>

        <button
          type="button"
          onClick={() => handleClick('location.city')}
          className={cn(
            'flex items-center gap-2 text-xs w-full text-left transition-colors',
            onFieldClick && 'cursor-pointer hover:text-orange-600',
          )}
        >
          <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="truncate">
            {location.city && location.country
              ? `${location.city}, ${countryDisplayName(location.country)}`
              : m.locationNotSet()}
          </span>
        </button>

        <button
          type="button"
          onClick={() => handleClick('price.kind')}
          className={cn(
            'text-xs font-medium w-full text-left transition-colors',
            onFieldClick && 'cursor-pointer hover:text-orange-600',
          )}
        >
          {formatPrice()}
        </button>
      </CardContent>
    </Card>
  )
}
